import React from 'react';
import Router from 'next/router';
import Head from 'next/head';
import { load } from 'cheerio';
import { ErrorLayout, MainLayout } from '@layouts';
import { getHelpContent } from '@modules/get-data';
import { ArticlePage } from '@sections/Help-Center-Content';
import { getErrorContent, redirect } from '@libs';
import { ErrorContent } from '@components/Help-Center-Components';
import { ErrorConnection } from '@components/Help-Center-Components/error-connection';
import { HELP_CENTER, IS_SERVER, PROCESSING_ITEMS } from '@constants';
import * as actions from '@store/actions';

const Article = ({ data, isCourse }) => <ArticlePage data={data} isCourse={isCourse} isShowCategory={false} />;

Article.getLayout = (page, { data, isCourse, locale, statusCode, isDisconnect }) => {
  const { translations, query } = page.props;

  if (isDisconnect) {
    return (
      <ErrorLayout>
        <MainLayout>
          <ErrorConnection />
        </MainLayout>
      </ErrorLayout>
    );
  }
  if (statusCode) {
    const error = getErrorContent({ code: statusCode });

    return (
      <ErrorLayout title={translations.t(error.title)}>
        <MainLayout reqQuery={query}>
          <ErrorContent error={error} locale={locale} />
        </MainLayout>
      </ErrorLayout>
    );
  }

  const htmlString = load(data.body);
  const imagesArray = htmlString('img')
    .map((_, el) => htmlString(el).attr('src'))
    .get();

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': `${data.caption}`,
    'datePublished': `${data.createdAt}`,
    'dateModified': `${data.modifiedAt}`,
    'image': imagesArray,
    'publisher': {
      '@type': 'Organization',
      'name': 'Планоплан',
      'url': 'https://planoplan.com',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': `${translations.t('site.help.title')}`,
        'item': `https://planoplan.com/${locale}/${HELP_CENTER.MAIN}`,
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': `${data.section_name}`,
        'item': `https://planoplan.com/${locale}/${HELP_CENTER.MAIN}/${data.section}`,
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': `${data.category_name}`,
        'item': `https://planoplan.com/${locale}/${HELP_CENTER.MAIN}/${data.section}/${data.category}`,
      },
      {
        '@type': 'ListItem',
        'position': 4,
        'name': `${data.caption}`,
      },
    ],
  };

  const metaTitle = data.meta_title || `${data.caption} ${translations.t('site.help.metaTitleTemplate.articlepage')}`;
  const metaDescription = isCourse
    ? `${translations.t('site.help.metaDescriptionTemplate.coursepage')} ${data.course.courseCaption}`
    : data.meta_description || `${data.description}`;

  return (
    <MainLayout reqQuery={query} title={metaTitle} description={metaDescription}>
      <Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      </Head>
      {page}
    </MainLayout>
  );
};

Article.getInitialProps = async ({ res, query, store }) => {
  const { course_id, article } = query;
  const { dispatch, getState } = store;
  const section = HELP_CENTER.TROUBLES;
  const {
    settings: { locale },
  } = getState();
  store.dispatch(actions.clearError('GET_HELP_CONTENT'));

  const isCourse = course_id !== undefined;

  if (article.length === 1) {
    const path = `/${locale}/${HELP_CENTER.MAIN}/${section}`;
    if (IS_SERVER && res) {
      redirect(path, {}, 301, res);
    } else {
      Router.push(path);
    }
    return {};
  }

  const data = await dispatch(
    getHelpContent({
      section,
      category: article[0],
      article: isCourse ? `${article[1]}/?course_id=${course_id}` : `${article[1]}`,
    })
  );
  const { errors } = getState();
  const error = errors[PROCESSING_ITEMS.GET_HELP_CONTENT];
  if (data.isDisconnect) {
    return { data, section, locale, isDisconnect: true };
  }
  if (error) {
    if (res && (error.code === 404 || error.code === 500)) {
      res.statusCode = error.code;
    }

    return { data, locale, statusCode: error.code || 404 };
  }

  return { data, isCourse, statusCode: undefined, query };
};

export default Article;
